import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { timePickerModal } from "analogue-time-picker";
import { Dropdown } from "react-bootstrap";
import {
  fetchConnectedUser,
  fetchMyOrders,
  markOrderAsPaid,
  sendReceipt,
  updateOrderpreparationTime,
  updateOrderStatus,
} from "../../../services/OrdersService";
import { toast } from "react-toastify";
import socket from "../../../services/socket";
import acceptedImg from "../../../assets/images/status/accepted.png";
import rejectedImg from "../../../assets/images/status/rejected.png";
import deliveredImg from "../../../assets/images/status/delivered.png";
import preparedImg from "../../../assets/images/status/prepared.png";
import inProgressImg from "../../../assets/images/status/in progress.png";

import tableImg from "../../../assets/images/order/table.png";
import totalImg from "../../../assets/images/order/total.png";
import tipsImg from "../../../assets/images/order/tips.png";
import creditCardImg from "../../../assets/images/order/card-2.png";
import cashImg from "../../../assets/images/order/cash-2.png";

import "../../../i18n";
import { useTranslation } from "react-i18next";
import { formatMoney } from "../../../utils/currency";

const statusImages = {
  accepted: acceptedImg,
  rejected: rejectedImg,
  delivered: deliveredImg,
  prepared: preparedImg,
  "in progress": inProgressImg,
};
function Orders() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("in progress");
  const [openModal, setOpenModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPreparationModal, setShowPreparationModal] = useState(false);
  const [preparationTime, setPreparationTime] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [ordersTimes, setOrdersTimes] = useState([]);
  const intervalsRef = useRef({});
  const sort = 10;
  const activePag = useRef(0);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [totalPages, setTotalPages] = useState([]);
  const [loadSubmit, setLoadSubmit] = useState(false);
  const [openOrderDetail, setOpenOrderDetails] = useState(false);
  const [inputValue, setInputValue] = useState(null);
  const [resendReceipt, setResendReceipt] = useState(false);
  const STATUS_OPTIONS = [
    { label: t("newOrder"), value: "in progress" },
    { label: t("accepted"), value: "accepted" },
    { label: t("rejected"), value: "rejected" },
    { label: t("prepared"), value: "prepared" },
    { label: t("delivered"), value: "delivered" },
  ];

  const handleSetPreparationTime = () => {
    if (!selectedOrder) return;
    setLoadSubmit(true);
    const time = parseInt(preparationTime, 10);
    if (isNaN(time) || time <= 0) {
      toast.error(t("warningPreparationTimeInput"));
      return;
    }

    updateOrderStatus(
      selectedOrder.orderNumber,
      "accepted",
      null, // rejectReason = null
      time // estimatedPreparationTime
    )
      .then(() => {
        setLoadSubmit(false);
        toast.success(t("OrderAcceptedWithPrepTime"));
        setShowPreparationModal(false);
        setPreparationTime("");
        refreshOrders();
      })
      .catch((err) => toast.error(err.message));
  };

  useEffect(() => {
    let isMounted = true;

    // 1️⃣ Charger les commandes initiales
    const loadOrders = async () => {
      try {
        const data = await fetchMyOrders();
        if (!isMounted) return;

        // ✅ Filtrage conditionnel sur les paiements "credit card"
        const filteredData = data.filter((order) => {
          if (order.payMethod === "credit card") {
            return !!order.payerName; // ne garder que si payerName existe
          }
          return true; // sinon garder normalement
        });

        const sorted = filteredData.sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt) -
            new Date(a.updatedAt || a.createdAt)
        );
        setOrders(sorted);

        // Initialiser ordersTimes
        const times = sorted
          .map((order) => {
            const estimatedTime = order?.estimatedPreparationTime;
            if (!estimatedTime || estimatedTime <= 0) return null;
            const totalSeconds = estimatedTime * 60;
            const orderTime = new Date(
              order.updatedAt || order.createdAt
            ).getTime();
            const elapsedSeconds = Math.floor((Date.now() - orderTime) / 1000);
            const remaining = Math.max(totalSeconds - elapsedSeconds, 0);

            return {
              orderNumber: order.orderNumber,
              remainingTime: remaining,
              initialPreparationTime: totalSeconds,
            };
          })
          .filter(Boolean); // 🚫 éviter les null
        setOrdersTimes(times);

        // Filtrer initial
        const filtered = sorted.filter((o) => o.status === selectedStatus);
        setFilteredOrders(filtered);
        paginateData(filtered, 0);
      } catch (err) {
        console.error(err);
      }
    };

    // 2️⃣ Configurer socket
    const setupSocket = async () => {
      const user = await fetchConnectedUser();
      const restaurantId = user.restaurantFK;
      if (!restaurantId) return;

      socket.emit("join-restaurant", restaurantId);

      socket.on("new-order", (order) => {
        if (order.restaurantId === restaurantId) {
          setOrders((prev) => {
            const exists = prev.some(
              (o) => o.orderNumber === order.orderNumber
            );
            if (exists) return prev;
            return [order, ...prev];
          });

          if (order.status === "accepted") {
            addOrderTimer(order);
          }
        }
      });

      socket.on("order-updated", (updatedOrder) => {
        setOrders((prev) =>
          prev.map((o) =>
            o.orderNumber === updatedOrder.orderNumber ? updatedOrder : o
          )
        );

        if (updatedOrder.status === "accepted") {
          addOrderTimer(updatedOrder);
        }

        if (
          ["prepared", "rejected", "delivered"].includes(updatedOrder.status)
        ) {
          clearOrderTimer(updatedOrder.orderNumber);
        }
      });
    };

    loadOrders();
    setupSocket();

    // 3️⃣ Gérer timers en fonction des commandes
    orders.forEach((order) => {
      if (
        order.status === "accepted" &&
        !intervalsRef.current[order.orderNumber]
      ) {
        const existing = ordersTimes.find(
          (t) => t.orderNumber === order.orderNumber
        );
        const totalSeconds =
          existing?.remainingTime || order.estimatedPreparationTime * 60;

        intervalsRef.current[order.orderNumber] = setInterval(() => {
          setOrdersTimes((prev) =>
            prev.map((t) => {
              if (t.orderNumber !== order.orderNumber) return t;

              const newRemaining = Math.max(t.remainingTime - 1, 0);

              if (newRemaining === 0) {
                clearInterval(intervalsRef.current[order.orderNumber]);
                delete intervalsRef.current[order.orderNumber];

                updateOrderStatus(order.orderNumber, "prepared")
                  .then(() => {
                    toast.success(
                      `Order ${order.orderNumber} prepared automatically!`
                    );
                    refreshOrders();
                  })
                  .catch((err) => toast.error(err.message));
              }

              return { ...t, remainingTime: newRemaining };
            })
          );
        }, 1000);

        if (!existing) {
          setOrdersTimes((prev) => [
            ...prev,
            {
              orderNumber: order.orderNumber,
              remainingTime: totalSeconds,
              initialPreparationTime: totalSeconds,
            },
          ]);
        }
      }
    });

    // 4️⃣ Recalcul ordersTimes quand commandes changent
    if (orders && orders.length > 0) {
      setOrdersTimes((prev) => {
        const updated = [...prev];

        orders.forEach((order) => {
          const exists = prev.some((t) => t.orderNumber === order.orderNumber);
          if (!exists && order.estimatedPreparationTime) {
            const totalSeconds = order.estimatedPreparationTime * 60;
            const orderTime = new Date(
              order.updatedAt || order.createdAt
            ).getTime();
            const elapsedSeconds = Math.floor((Date.now() - orderTime) / 1000);
            const remaining = Math.max(totalSeconds - elapsedSeconds, 0);

            updated.push({
              orderNumber: order.orderNumber,
              remainingTime: remaining,
              initialPreparationTime: totalSeconds,
            });
          }
        });

        return updated;
      });

      const filtered = orders.filter((o) => o.status === selectedStatus);
      setFilteredOrders(filtered);
      paginateData(filtered, 0);
    }

    // 🔚 cleanup global
    return () => {
      isMounted = false;
      Object.values(intervalsRef.current).forEach(clearInterval);
      socket.off("new-order");
      socket.off("order-updated");
    };
  }, [orders, selectedStatus]);

  // --- Fonction pour ajouter timer pour une commande ---
  const addOrderTimer = (order) => {
    setOrdersTimes((prev) => {
      const exists = prev.find((t) => t.orderNumber === order.orderNumber);
      if (exists) return prev;

      // 🚫 Si pas de durée définie, on ne lance pas de timer
      if (
        !order.estimatedPreparationTime ||
        order.estimatedPreparationTime <= 0
      ) {
        console.warn(
          `⚠️ Pas de estimatedPreparationTime pour la commande ${order.orderNumber}`
        );
        return prev;
      }

      const totalSeconds = order.estimatedPreparationTime * 60;

      startTimer(order.orderNumber, totalSeconds);

      return [
        ...prev,
        {
          orderNumber: order.orderNumber,
          remainingTime: totalSeconds,
          initialPreparationTime: totalSeconds,
        },
      ];
    });
  };

  const startTimer = (orderNumber, totalSeconds) => {
    if (intervalsRef.current[orderNumber]) return;

    intervalsRef.current[orderNumber] = setInterval(() => {
      setOrdersTimes((prev) =>
        prev.map((t) => {
          if (t.orderNumber !== orderNumber) return t;

          const newRemaining = Math.max(t.remainingTime - 1, 0);

          if (newRemaining === 0) {
            // ⏰ Stop timer
            clearInterval(intervalsRef.current[orderNumber]);
            delete intervalsRef.current[orderNumber];

            // ✅ Mettre la commande en "prepared"
            updateOrderStatus(orderNumber, "prepared")
              .then(() => {
                toast.success(`Order ${orderNumber} prepared automatically!`);
                refreshOrders();
              })
              .catch((err) => toast.error(err.message));
          }

          return { ...t, remainingTime: newRemaining };
        })
      );
    }, 1000);
  };

  const clearOrderTimer = (orderNumber) => {
    if (intervalsRef.current[orderNumber]) {
      clearInterval(intervalsRef.current[orderNumber]);
      delete intervalsRef.current[orderNumber];
    }
  };

  // --- Cleanup global à la destruction ---
  useEffect(() => {
    return () => {
      Object.values(intervalsRef.current).forEach(clearInterval);
    };
  }, []);

  const paginateData = (data, page) => {
    activePag.current = page;
    const start = page * sort;
    const end = start + sort;
    setCurrentPageData(data.slice(start, end));
    setTotalPages(Array(Math.ceil(data.length / sort)).fill());
  };

  const handlePageClick = (index) => {
    paginateData(filteredOrders, index);
  };

  const handleStatusFilter = (statusValue) => {
    setSelectedStatus(statusValue);
    const newFiltered = orders.filter((order) => order.status === statusValue);
    setFilteredOrders(newFiltered);
    paginateData(newFiltered, 0);
  };

  useEffect(() => {
    if (openModal) {
      timePickerModal({
        width: "300px",
        time: { hour: 12, minute: 0 },
      });
    }
  }, [openModal]);

  const handleRejectOrder = () => {
    if (!selectedOrder) return;
    updateOrderStatus(selectedOrder.orderNumber, "rejected", rejectReason)
      .then(() => {
        toast.error(t("orderRejected"));
        refreshOrders();
        setShowRejectModal(false);
        setRejectReason("");
      })
      .catch((err) => toast.error(err.message));
  };

  const refreshOrders = async (status = selectedStatus) => {
    try {
      const data = await fetchMyOrders();
      const sorted = data.sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt) -
          new Date(a.updatedAt || a.createdAt)
      );

      setOrders(sorted);

      const filtered = sorted.filter((order) => order.status === status);
      setFilteredOrders(filtered);
      paginateData(filtered, 0);
    } catch (error) {
      console.error("Erreur lors de l'actualisation des commandes :", error);
    }
  };

  const updatePreparationTime = (orderNumber, newTime) => {
    const seconds = parseInt(newTime, 10) * 60;

    // 1️⃣ Mettre à jour le state local immédiatement pour que le timer se mette à jour
    setOrdersTimes((prev) => {
      const exists = prev.find((t) => t.orderNumber === orderNumber);
      if (exists) {
        return prev.map((t) =>
          t.orderNumber === orderNumber
            ? { ...t, remainingTime: seconds, initialPreparationTime: seconds }
            : t
        );
      } else {
        return [
          ...prev,
          {
            orderNumber,
            remainingTime: seconds,
            initialPreparationTime: seconds,
          },
        ];
      }
    });

    // 2️⃣ Appeler le backend pour sauvegarder la modification en temps réel
    updateOrderpreparationTime(orderNumber, newTime)
      .then((data) => {
        console.log(
          "✅ Temps de préparation mis à jour sur le serveur :",
          data
        );
      })
      .catch((err) => {
        console.error("❌ Erreur lors de la mise à jour sur le serveur :", err);
      });
  };

  function getProgressColor(remainingTime, totalTime) {
    const ratio = remainingTime / totalTime;

    if (ratio > 0.5) return "#FA8072";
    if (ratio > 0.3) return "#FFA500";
    if (ratio > 0.1) return "#FFD700";
    return "#4caf50";
  }

  const statusTranslationMap = {
    "in progress": "newOrder",
    accepted: "accepted",
    rejected: "rejected",
    prepared: "prepared",
    delivered: "delivered",
  };

  return (
    <>
      <div className="d-sm-flex mb-lg-4 mb-2 align-items-center justify-content-between flex-wrap gap-2" data-tour="tour-page-orders">
        <div className="d-flex flex-wrap gap-2 align-items-center orders-page-toolbar">
          <Link
            to="/orders-board"
            className="btn btn-outline-primary btn-rounded btn-sm"
          >
            <i className="fas fa-columns me-2" aria-hidden />
            {t("ordersBoard")}
          </Link>
        </div>
        <Dropdown className="dropdown mb-2 ms-auto me-3">
          <Dropdown.Toggle className="i-false btn btn-primary btn-rounded light">
            <i className="las la-bolt scale5 me-2" />
            {STATUS_OPTIONS.find((opt) => opt.value === selectedStatus)
              ?.label || "All Status"}
            <i className="las la-angle-down ms-3" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {STATUS_OPTIONS.map((option, index) => (
              <Dropdown.Item
                key={index}
                onClick={() => handleStatusFilter(option.value)}
              >
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="table-responsive rounded card-table">
            <div id="orders">
              <table className="table border-no order-table mb-4">
                <thead>
                  <tr>
                    <th>{t("orderNb")}</th>
                    <th>{t("tableNb")}</th>
                    <th>{t("date")}</th>
                    <th>{t("products")}</th>
                    <th>{t("items")}</th>
                    <th>{t("amount")}</th>
                    <th>{t("statusOrder")}</th>
                    <th>{t("payment")}</th>
                    {currentPageData.some((o) => o.status === "accepted") && (
                      <th>{t("prepTime")}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.map((order, idx) => {
                    const orderTimeData = ordersTimes.find(
                      (t) => t.orderNumber === order.orderNumber
                    );

                    const remainingTime = orderTimeData?.remainingTime || 0;
                    const initialTime =
                      orderTimeData?.initialPreparationTime ||
                      (order.estimatedPreparationTime || 1) * 60;

                    return (
                      <tr
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedOrder(order);
                          setOpenOrderDetails(true);
                        }}
                      >
                        <td>{order.orderNumber}</td>
                        <td>{order.tableNumber || "-"}</td>
                        <td>{new Date(order.createdAt).toLocaleString()}</td>
                        <td>
                          {order.products.map((p, i) => {
                            const lang = (
                              localStorage.getItem("currentLanguage") || "en"
                            )
                              .toLowerCase()
                              .trim();

                            // Trouver la correspondance exacte
                            const details = p.product?.productDetails?.find(
                              (d) =>
                                (d.language || "").toLowerCase().trim() === lang
                            );

                            // Si details est trouvé, on affiche le titre
                            const title =
                              details?.productTitle ||
                              `Product (${lang.toUpperCase()} translation not available)`;

                            return (
                              <div key={i} style={{ marginBottom: 6 }}>
                                <div
                                  style={{ fontWeight: "bold", color: "#333" }}
                                >
                                  {p.size && (
                                    <span
                                      style={{
                                        backgroundColor: "#FFB6A0",
                                        color: "#fff",
                                        padding: "2px 6px",
                                        borderRadius: 4,
                                        marginRight: 6,
                                        fontSize: 14,
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {p.size}
                                    </span>
                                  )}
                                  {title} × {p.quantity}
                                </div>
                              </div>
                            );
                          })}
                        </td>

                        <td>
                          {order.products.map((p, i) => (
                            <div key={i}>
                              {p.selectedItems?.length > 0 ? (
                                <ul
                                  style={{ paddingLeft: 16, marginBottom: 6 }}
                                >
                                  {p.selectedItems.map((si, idx) => {
                                    const lang = (
                                      localStorage.getItem("currentLanguage") ||
                                      "en"
                                    )
                                      .toLowerCase()
                                      .trim();

                                    const details =
                                      si.item?.itemDetails?.find(
                                        (d) =>
                                          (d.language || "")
                                            .toLowerCase()
                                            .trim() === lang
                                      ) || si.item?.itemDetails?.[0]; // fallback sur la première langue existante

                                    const title = details?.itemTitle || "Item";

                                    return (
                                      <li
                                        key={idx}
                                        style={{
                                          fontSize: "0.85rem",
                                          color: "#555",
                                        }}
                                      >
                                        {title}
                                      </li>
                                    );
                                  })}
                                </ul>
                              ) : (
                                <span
                                  style={{ fontSize: "0.85rem", color: "#aaa" }}
                                >
                                  –
                                </span>
                              )}
                            </div>
                          ))}
                        </td>

                        <td>
                          {order.totalPrice
                            ? formatMoney(order.totalPrice)
                            : `-`}
                        </td>
                        <td>
                          <span className="text-primary d-flex align-items-center gap-1">
                            <svg
                              width={12}
                              height={12}
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <circle cx={6} cy={6} r={6} fill="#EA7A9A" />
                            </svg>
                            {t(
                              {
                                "in progress": "newOrder",
                                accepted: "accepted",
                                rejected: "rejected",
                                prepared: "prepared",
                                delivered: "delivered",
                              }[order.status] || "pending"
                            )}
                          </span>
                        </td>

                        <td>
                          {order.isPaid
                            ? t("paid")
                            : order.payMethod === "credit card"
                            ? t("received")
                            : t("notPaid")}
                        </td>

                        {/* Preparation Time pour status accepted */}
                        {order.status === "accepted" && (
                          <td>
                            {/* Input qui montre le temps restant en minutes */}
                            {remainingTime > 0 && (
                              <input
                                type="number"
                                min="1"
                                value={
                                  inputValue ?? Math.ceil(remainingTime / 60)
                                }
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const newValue = e.target.value;
                                  setInputValue(newValue); // Met à jour l’input localement
                                  updatePreparationTime(
                                    order.orderNumber,
                                    newValue
                                  ); // ⚡ Mise à jour immédiate
                                }}
                                style={{
                                  width: "60px",
                                  padding: "2px 4px",
                                  fontSize: "0.85rem",
                                  textAlign: "center",
                                }}
                              />
                            )}

                            {/* Texte temps restant */}
                            {remainingTime > 0 && (
                              <div
                                style={{
                                  fontWeight: "bold",
                                  fontSize: 14,
                                  marginTop: 4,
                                }}
                              >
                                {t("remaining")}:{" "}
                                {Math.floor(remainingTime / 60)}m{" "}
                                {remainingTime % 60}s
                              </div>
                            )}

                            {/* Barre de progression */}
                            {remainingTime > 0 && (
                              <div
                                style={{
                                  height: 8,
                                  width: "100%",
                                  backgroundColor: "#ddd",
                                  borderRadius: 4,
                                  marginTop: 4,
                                }}
                              >
                                <div
                                  style={{
                                    height: "100%",
                                    width: `${
                                      (remainingTime / initialTime) * 100
                                    }%`,
                                    backgroundColor: getProgressColor(
                                      remainingTime,
                                      initialTime
                                    ),
                                    borderRadius: 4,
                                    transition:
                                      "width 1s linear, background-color 0.5s ease",
                                  }}
                                />
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="d-sm-flex justify-content-between align-items-center mt-3">
                <div>
                  {t("showing")} {activePag.current * sort + 1} {t("to")}{" "}
                  {Math.min(
                    (activePag.current + 1) * sort,
                    filteredOrders.length
                  )}{" "}
                  of {filteredOrders.length} {t("entries")}
                </div>
                <div>
                  <Link
                    to="#"
                    onClick={() =>
                      activePag.current > 0 &&
                      handlePageClick(activePag.current - 1)
                    }
                    className="paginate_button previous"
                  >
                    {t("previous")}
                  </Link>
                  <span>
                    {totalPages.map((_, i) => (
                      <Link
                        key={i}
                        to="#"
                        className={`paginate_button ${
                          activePag.current === i ? "current" : ""
                        }`}
                        onClick={() => handlePageClick(i)}
                      >
                        {" "}
                        {i + 1}{" "}
                      </Link>
                    ))}
                  </span>

                  <Link
                    to="#"
                    onClick={() =>
                      activePag.current + 1 < totalPages.length &&
                      handlePageClick(activePag.current + 1)
                    }
                    className="paginate_button next"
                  >
                    {t("next")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        {showRejectModal && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "#fff",
                padding: 20,
                borderRadius: 8,
                maxWidth: 500,
                width: "100%",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              }}
            >
              <h3 style={{ marginBottom: 10 }}>{t("rejectReason")}</h3>
              <textarea
                rows={3}
                value={rejectReason}
                onChange={(e) => {
                  if (e.target.value.length <= 150) {
                    setRejectReason(e.target.value);
                  }
                }}
                placeholder={t("rejectInput")}
                style={{
                  width: "100%",
                  resize: "vertical",
                  padding: 8,
                  fontSize: "1rem",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                }}
              />
              <div
                style={{
                  fontSize: "0.85rem",
                  marginTop: 4,
                  textAlign: "right",
                }}
              >
                {150 - rejectReason.length} {t("charactersRemaining")}
              </div>
              <div
                style={{
                  marginTop: 16,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                <button
                  onClick={() => setShowRejectModal(false)}
                  style={{
                    backgroundColor: "#FA8072",
                    border: "none",
                    padding: "6px 12px",
                    borderRadius: 4,
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  {t("cancel")}
                </button>
                <button
                  onClick={handleRejectOrder}
                  disabled={!rejectReason.trim()}
                  style={{
                    backgroundColor: "transparent",
                    border: "1px solid #FA8072",
                    color: "#FA8072",
                    padding: "6px 12px",
                    borderRadius: 4,
                    cursor: rejectReason.trim() ? "pointer" : "not-allowed",
                  }}
                >
                  {t("confirmRejection")}
                </button>
              </div>
            </div>
          </div>
        )}

        {showPreparationModal && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "#fff",
                padding: 20,
                borderRadius: 8,
                maxWidth: 400,
                width: "100%",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              }}
            >
              <h3 style={{ marginBottom: 10 }}>{t("setPreparationTime")}</h3>
              <input
                type="number"
                value={preparationTime}
                onChange={(e) => setPreparationTime(e.target.value)}
                placeholder={t("prepTimeInput")}
                style={{
                  width: "100%",
                  padding: 8,
                  fontSize: "1rem",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                }}
              />
              <div
                style={{
                  marginTop: 16,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                <button
                  onClick={() => setShowPreparationModal(false)}
                  style={{
                    backgroundColor: "#FA8072",
                    border: "none",
                    padding: "6px 12px",
                    borderRadius: 4,
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  {t("cancel")}
                </button>
                <button
                  onClick={handleSetPreparationTime}
                  // disabled={!preparationTime.trim()}
                  style={{
                    backgroundColor: "transparent",
                    border: "1px solid #FA8072",
                    color: "#FA8072",
                    padding: "6px 12px",
                    borderRadius: 4,
                    cursor: preparationTime.trim() ? "pointer" : "not-allowed",
                  }}
                >
                  {loadSubmit ? t("loading") + "..." : t("confirm")}
                </button>
              </div>
            </div>
          </div>
        )}

        {openOrderDetail && selectedOrder && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            // onClick={() => setOpenOrderDetails(false)}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "#fff",
                borderRadius: 8,
                maxWidth: 600,
                width: "100%",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                overflow: "hidden",
              }}
            >
              {/* --- Header avec background dynamique --- */}
              <div
                style={{
                  position: "relative",
                  backgroundImage: `url(${
                    statusImages[selectedOrder.status] || inProgressImg
                  })`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  height: 180,
                  color: "#fff",
                  display: "flex",
                  alignItems: "flex-start", // 👈 aligner à gauche
                  justifyContent: "flex-start", // 👈 aligner en haut
                  flexDirection: "column",
                  padding: "16px", // 👈 petite marge intérieure
                  borderRadius: 10,
                }}
              >
                {/* overlay sombre */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                />

                <p
                  style={{
                    position: "relative",
                    zIndex: 2,
                    margin: 0,
                    fontWeight: "bold",
                    fontSize: 18,
                    textTransform: "capitalize",
                  }}
                >
                  {selectedOrder.status}
                </p>
                <p style={{ position: "relative", zIndex: 2, margin: 0 }}>
                  {new Date(selectedOrder.createdAt).toLocaleString()}
                </p>
              </div>
              <h3
                style={{
                  position: "relative",
                  zIndex: 2,
                  marginTop: "3%",
                  marginLeft: "1%",
                }}
              >
                {t("N")}° {selectedOrder.orderNumber}
              </h3>

              {/* --- Contenu sous l'image --- */}
              <div style={{ padding: 20 }}>
                {/* --- Ligne avec 3 infos verticales --- */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "stretch",
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  {/* Customer Name */}
                  {selectedOrder.payerName && (
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #ccc",
                        borderRadius: 8,
                        padding: 12,
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: "#f0f0f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: 8,
                          border: "2px solid #333",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#333"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </div>
                      <span style={{ fontSize: 14, fontWeight: "500" }}>
                        {selectedOrder.payerName}
                      </span>
                    </div>
                  )}

                  {/* Table */}
                  {selectedOrder.tableNumber && (
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #ccc",
                        borderRadius: 8,
                        padding: 12,
                      }}
                    >
                      <img
                        src={tableImg}
                        alt="Table"
                        style={{ width: 32, height: 32, marginBottom: 8 }}
                      />
                      <span style={{ fontSize: 14, fontWeight: "500" }}>
                        {t("table")}: {selectedOrder.tableNumber}
                      </span>
                    </div>
                  )}

                  {/* Total */}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #ccc",
                      borderRadius: 8,
                      padding: 12,
                    }}
                  >
                    <img
                      src={totalImg}
                      alt="Total"
                      style={{ width: 32, height: 32, marginBottom: 8 }}
                    />
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: "500",
                        marginRight: 15,
                      }}
                    >
                      {formatMoney(selectedOrder.finalPrice || 0)}
                    </span>
                  </div>

                  {/* Tips (si payé par carte) */}
                  {selectedOrder.payMethod === "credit card" && (
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #ccc",
                        borderRadius: 8,
                        padding: 12,
                      }}
                    >
                      <img
                        src={tipsImg}
                        alt="Tips"
                        style={{ width: 32, height: 32, marginBottom: 8 }}
                      />
                      <span style={{ fontSize: 14, fontWeight: "500" }}>
                        {selectedOrder.tipsPercentage
                          ? selectedOrder.tipsPercentage + "%"
                          : formatMoney(selectedOrder.tipsAmount || 0)}
                      </span>
                    </div>
                  )}
                </div>
                {/* --- Méthode de paiement --- */}
                <div
                  style={{
                    marginTop: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 12,
                    border: "1px solid #ccc",
                    borderRadius: 8,
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <img
                    src={
                      selectedOrder.payMethod === "credit card"
                        ? creditCardImg
                        : selectedOrder.payMethod === "cash"
                        ? cashImg
                        : otherPayImg
                    }
                    alt="Payment"
                    style={{ width: 32, height: 32 }}
                  />
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: "600",
                      textTransform: "capitalize",
                    }}
                  >
                    {selectedOrder.payMethod}
                  </span>
                </div>
                {/* --- État de paiement --- */}
                <div
                  style={{
                    marginTop: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 12,
                    border: "1px solid #ccc",
                    borderRadius: 8,
                    backgroundColor: selectedOrder.isPaid
                      ? "#d4edda" // vert clair si payé
                      : selectedOrder.payMethod === "credit card"
                      ? "#fff3cd" // jaune clair si paiement par carte mais non payé
                      : "#f8d7da", // rouge clair si non payé et autre méthode
                  }}
                >
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: "600",
                      textTransform: "capitalize",
                      color: selectedOrder.isPaid
                        ? "#155724"
                        : selectedOrder.payMethod === "credit card"
                        ? "#856404" // texte brun foncé sur fond jaune clair
                        : "#721c24", // texte rouge sur fond rouge clair
                    }}
                  >
                    {selectedOrder.isPaid
                      ? t("paid")
                      : selectedOrder.payMethod === "credit card"
                      ? t("received")
                      : t("notPaid")}
                  </span>
                </div>

                {/* --- Lien facture PDF --- */}
                {selectedOrder.status !== "in progress" &&
                  selectedOrder.invoicePdfUrl && (
                    <div
                      style={{
                        marginTop: 16,
                        padding: 12,
                        border: "1px solid #ccc",
                        borderRadius: 8,
                        backgroundColor: "#f0f8ff", // léger bleu pour signaler le PDF
                        textAlign: "center",
                      }}
                    >
                      <a
                        href={selectedOrder.invoicePdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: "none",
                          fontWeight: "600",
                          color: "#007bff",
                          cursor: "pointer",
                        }}
                      >
                        {t("DownloadInvoicePDF")}
                      </a>
                    </div>
                  )}

                {/* --- Produits --- */}
                <h4 style={{ marginTop: 16 }}>{t("products")}</h4>
                <ul>
                  {selectedOrder.products.map((p, i) => (
                    <li key={i}>
                      {p.product?.productDetails?.[0]?.productTitle} ×{" "}
                      {p.quantity}
                      {p.size && <span> (Size: {p.size})</span>}
                    </li>
                  ))}
                </ul>

                {/* --- Bouton --- */}
                <div
                  style={{
                    marginTop: 16,
                    textAlign: "right",
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                  }}
                >
                  {/* Close : toujours affiché */}
                  <button
                    onClick={() => setOpenOrderDetails(false)}
                    style={{
                      backgroundColor: "#a2a1a1ff",
                      border: "none",
                      padding: "6px 12px",
                      borderRadius: 4,
                      color: "white",
                      cursor: "pointer",
                      borderWidth: 2,
                    }}
                  >
                    {t("close")}
                  </button>

                  {/* Si la commande est "in progress" */}
                  {selectedOrder.status === "in progress" && (
                    <>
                      <button
                        onClick={() => {
                          setOpenOrderDetails(false);
                          setShowPreparationModal(true);
                          console.log("to open open");
                        }}
                        style={{
                          backgroundColor: "#fc8474", // saumon
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: 4,
                          color: "white",
                          cursor: "pointer",
                        }}
                      >
                        {t("accept")}
                      </button>
                      <button
                        onClick={() => {
                          setOpenOrderDetails(false);
                          setShowRejectModal(true);
                          console.log("to open open");
                        }}
                        style={{
                          backgroundColor: "#e55d50", // rouge harmonieux avec saumon
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: 4,
                          color: "white",
                          cursor: "pointer",
                        }}
                      >
                        {t("reject")}
                      </button>
                    </>
                  )}

                  {/* Si la commande est "accepted" */}
                  {selectedOrder.status === "accepted" && (
                    <button
                      onClick={() => {
                        updateOrderStatus(
                          selectedOrder.orderNumber,
                          "prepared"
                        );
                        setOpenOrderDetails(false);
                      }}
                      style={{
                        backgroundColor: "#fc8474", // bleu
                        border: "none",
                        padding: "6px 12px",
                        borderRadius: 4,
                        color: "white",
                        cursor: "pointer",
                      }}
                    >
                      {t("prepared")}
                    </button>
                  )}

                  {/* Si la commande est "prepared" */}
                  {selectedOrder.status === "prepared" && (
                    <button
                      onClick={() => {
                        updateOrderStatus(
                          selectedOrder.orderNumber,
                          "delivered"
                        );
                        setOpenOrderDetails(false);
                      }}
                      style={{
                        backgroundColor: "#fc8474", // turquoise
                        border: "none",
                        padding: "6px 12px",
                        borderRadius: 4,
                        color: "white",
                        cursor: "pointer",
                      }}
                    >
                      {t("delivered")}
                    </button>
                  )}

                  {/* Si la commande est "delivered" */}
                  {selectedOrder.status === "delivered" &&
                    !selectedOrder.isPaid && (
                      <button
                        onClick={() => {
                          markOrderAsPaid(selectedOrder.orderNumber);

                          setOpenOrderDetails(false);
                        }}
                        style={{
                          backgroundColor: "#fc8474", // jaune
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: 4,
                          color: "#fff",
                          cursor: "pointer",
                        }}
                      >
                        {t("pay")}
                      </button>
                    )}

                  {/* Si la commande est "delivered" → bouton pour renvoyer le reçu */}
                  {selectedOrder.status === "delivered" &&
                    selectedOrder.isPaid && (
                      <button
                        onClick={async () => {
                          try {
                            setResendReceipt(true);
                            await sendReceipt(selectedOrder.orderNumber);
                            toast.success(
                              "Receipt successfully resent to the client!"
                            );
                            setResendReceipt(false);

                            setOpenOrderDetails(false);
                          } catch (err) {
                            toast.error(err.message);
                          }
                        }}
                        style={{
                          backgroundColor: "#fc8474", // vert pour succès
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: 4,
                          color: "white",
                          cursor: "pointer",
                        }}
                      >
                        {resendReceipt ? t("loading") : t("resendReceipt")}
                      </button>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Orders;
